import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Play, Edit, Wand2, Sparkles, DollarSign, Heart, Zap, Home, Briefcase, Flame } from 'lucide-react';
import { ReelTemplate, REEL_TEMPLATES } from './ReelTemplates';
import EnhancedManifestationCreator from './EnhancedManifestationCreator';
import EnhancedManifestationPlayer from './EnhancedManifestationPlayer';

interface ManifestationTemplate {
  id: string;
  title: string;
  icon: React.ReactNode;
  category: string;
  description: string;
  affirmations: string[];
  subliminalWords: string[];
  sampleImages: string[];
  suggestedTemplate: ReelTemplate;
  color: string;
  representativeImage: string;
}

const MANIFESTATION_TEMPLATES: ManifestationTemplate[] = [
  {
    id: 'wealth',
    title: 'Financial Abundance',
    icon: <DollarSign className="w-6 h-6" />,
    category: 'Wealth',
    description: 'Manifest prosperity, abundance, and financial freedom',
    affirmations: [
      'Money flows to me effortlessly and abundantly',
      'I am a powerful money magnet attracting wealth now',
      'Massive abundance is my divine birthright',
      'I receive unlimited wealth from multiple sources',
      'Financial freedom is my reality today',
      'I effortlessly attract six-figure opportunities',
      'My bank account overflows with prosperity',
      'I am worthy of extraordinary wealth',
      'Money loves me and comes to me easily',
      'I create wealth doing what I love',
      'Abundance flows to me like a river',
      'I am financially free and living luxuriously',
      'Prosperity is my natural state of being',
      'I attract millionaire opportunities daily',
      'My wealth grows exponentially every day',
      'I am a powerful creator of infinite abundance',
      'Money multiplies in my hands',
      'I deserve and receive massive success',
      'Wealth pours into my life from all directions',
      'I am living my richest, most abundant life now'
    ],
    subliminalWords: ['rich', 'abundant', 'prosperous', 'wealthy', 'millionaire', 'success', 'luxury', 'opulent'],
     sampleImages: [
       'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=1200&q=80', // white yacht turquoise water
       'https://images.unsplash.com/photo-1612198188060-c7c2a3b66eae?w=1200&q=80', // colorful gemstones
       'https://images.unsplash.com/photo-1580983561371-7f4b242d8ec0?w=1200&q=80', // hundred dollar bills
       'https://images.unsplash.com/photo-1605902394069-ff2ae2430e62?w=1200&q=80', // red ferrari
       'https://images.unsplash.com/photo-1523878288860-7ad281611901?w=1200&q=80', // luxury beachfront villa
       'https://images.unsplash.com/photo-1545342323-2d0d22d4e0d1?w=1200&q=80', // paradise beach aerial
       'https://images.unsplash.com/photo-1540541338287-41700207dee6?w=1200&q=80', // luxury sports car coastal
       'https://images.unsplash.com/photo-1471086569966-db3eebc25a59?w=1200&q=80', // swiss alps mountains lake
       'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=1200&q=80', // gold bars stacked
       'https://images.unsplash.com/photo-1561414927-6d86591d0c4f?w=1200&q=80', // maldives overwater bungalow
       'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=1200&q=80', // private jet interior
       'https://images.unsplash.com/photo-1582719471137-c3967ffb1c42?w=1200&q=80', // luxury jewelry diamonds
       'https://images.unsplash.com/photo-1580274455191-1c62238fa333?w=1200&q=80', // orange lamborghini
       'https://images.unsplash.com/photo-1583286814049-6ccf5b2ff490?w=1200&q=80', // white yacht ocean
       'https://images.unsplash.com/photo-1534951009808-766178b47a4f?w=1200&q=80', // dubai burj khalifa
       'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200&q=80', // tropical beach paradise
       'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&q=80', // mountain lake landscape
       'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=1200&q=80', // money cash bills
       'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=1200&q=80', // modern luxury mansion
       'https://images.unsplash.com/photo-1549294413-26f195200c16?w=1200&q=80', // gold bars treasure
       'https://images.unsplash.com/photo-1611117775350-ac3950990985?w=1200&q=80', // luxury helicopter
       'https://images.unsplash.com/photo-1574169208507-84376144848b?w=1200&q=80', // ocean sunset luxury
       'https://images.unsplash.com/photo-1552960394-c81add8de8b8?w=1200&q=80', // luxury champagne celebration
       'https://images.unsplash.com/photo-1606402179428-a57976d71fa4?w=1200&q=80', // diamond ring luxury
       'https://images.unsplash.com/photo-1565372195458-9de0b320ef04?w=1200&q=80', // rolls royce luxury
       'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&q=80', // stack of cash
       'https://images.unsplash.com/photo-1596003906949-67221c37965c?w=1200&q=80', // luxury penthouse city
       'https://images.unsplash.com/photo-1539635278303-d4002c07eae3?w=1200&q=80', // tropical island aerial
       'https://images.unsplash.com/photo-1509023464722-18d996393ca8?w=1200&q=80', // maldives luxury resort
       'https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?w=1200&q=80' // luxury gold watch
     ],
    suggestedTemplate: REEL_TEMPLATES[1],
    color: 'from-secondary to-secondary',
    representativeImage: 'https://images.unsplash.com/photo-1621981386829-9b458a2cddde?w=400&q=80'
  },
  {
    id: 'love',
    title: 'Soulmate Connection',
    icon: <Heart className="w-6 h-6" />,
    category: 'Love',
    description: 'Attract your perfect partner and deep, lasting love',
    affirmations: [
      'My soulmate is drawn to me magnetically right now',
      'I am deeply loved, cherished, and adored',
      'My perfect partner finds me effortlessly',
      'I attract passionate, extraordinary love',
      'I am worthy of epic, soul-deep romance',
      'Love flows to me like a powerful force',
      'My heart is open to receive true love now',
      'I magnetize my divine soulmate today',
      'We are destined to find each other',
      'Perfect, passionate love is mine now',
      'I deserve the most beautiful love story',
      'My soulmate adores every part of me',
      'We connect on every level - mind, body, soul',
      'Love surrounds and fills me completely',
      'I am irresistible to my perfect match',
      'My soulmate is searching for me too',
      'I radiate magnetic love energy',
      'True love finds me at the perfect time',
      'I am loved unconditionally and completely',
      'We are creating our magical love story now'
    ],
    subliminalWords: ['soulmate', 'beloved', 'romance', 'passion', 'connection', 'unity', 'devoted', 'cherished'],
    sampleImages: [
      'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=1200&q=80',
      'https://images.unsplash.com/photo-1518568814500-bf0f8d125f46?w=1200&q=80',
      'https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?w=1200&q=80',
      'https://images.unsplash.com/photo-1522673607211-8e86e5f12ee0?w=1200&q=80',
      'https://images.unsplash.com/photo-1518199266791-5375a83190b7?w=1200&q=80',
      'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=1200&q=80',
      'https://images.unsplash.com/photo-1617750118726-7092431af3e0?w=1200&q=80',
      'https://images.unsplash.com/photo-1474552226712-ac0f0961a954?w=1200&q=80',
      'https://images.unsplash.com/photo-1502139214982-d0ad755818d8?w=1200&q=80',
      'https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=1200&q=80',
      'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=1200&q=80',
      'https://images.unsplash.com/photo-1530103043960-ef38714abb15?w=1200&q=80',
      'https://images.unsplash.com/photo-1545315003-c5ad6226c272?w=1200&q=80',
      'https://images.unsplash.com/photo-1520452112805-c6692c840af0?w=1200&q=80',
      'https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&q=80',
      'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=1200&q=80',
      'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1200&q=80',
      'https://images.unsplash.com/photo-1538662944735-3262aac6a0a6?w=1200&q=80',
      'https://images.unsplash.com/photo-1526512340740-9217d0159da9?w=1200&q=80',
      'https://images.unsplash.com/photo-1529636798458-92182e662485?w=1200&q=80',
      'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1200&q=80',
      'https://images.unsplash.com/photo-1518568814500-bf0f8d125f46?w=1200&q=80',
      'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=1200&q=80',
      'https://images.unsplash.com/photo-1510759591315-6425cba413fe?w=1200&q=80',
      'https://images.unsplash.com/photo-1542396601-dca920ea2807?w=1200&q=80',
      'https://images.unsplash.com/photo-1532712938310-34cb3982ef74?w=1200&q=80',
      'https://images.unsplash.com/photo-1504194569091-4e1d3e9e1d8c?w=1200&q=80',
      'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=1200&q=80',
      'https://images.unsplash.com/photo-1523438885200-e635ba2c371e?w=1200&q=80',
      'https://images.unsplash.com/photo-1516455590571-18256e5bb9ff?w=1200&q=80'
    ],
    suggestedTemplate: REEL_TEMPLATES[5],
    color: 'from-muted to-ring',
    representativeImage: 'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=400&q=80'
  },
  {
    id: 'success',
    title: 'Career Success',
    icon: <Briefcase className="w-6 h-6" />,
    category: 'Career',
    description: 'Achieve professional excellence and recognition',
    affirmations: [
      'I am unstoppable in my career success',
      'I am a powerful, visionary leader',
      'Incredible opportunities come to me daily',
      'I excel brilliantly in everything I do',
      'My career skyrockets to extraordinary heights',
      'I am recognised as an industry leader',
      'Promotions and raises come to me easily',
      'I lead with unshakeable confidence',
      'Success follows me everywhere I go',
      'I achieve impossible goals effortlessly',
      'I am highly valued and irreplaceable',
      'My skills are in massive demand',
      'I make million-dollar decisions with ease',
      'My career growth is explosive and unstoppable',
      'I inspire and transform everyone around me',
      'I consistently exceed every expectation',
      'Recognition and respect flow to me naturally',
      'I am a groundbreaking visionary in my field',
      'My legendary success is inevitable',
      'I create extraordinary value and impact'
    ],
    subliminalWords: ['success', 'achievement', 'promotion', 'recognition', 'excellence', 'mastery', 'leadership', 'visionary'],
    sampleImages: [
      'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=1200&q=80', // woman architect reviewing plans
      'https://images.unsplash.com/photo-1559223607-a43c990c71a3?w=1200&q=80', // diverse medical professionals
      'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1200&q=80', // black woman doctor smiling
      'https://images.unsplash.com/photo-1581093804475-577d72e38aa0?w=1200&q=80', // chef in professional kitchen
      'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=1200&q=80', // strong woman entrepreneur
      'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=1200&q=80', // diverse team celebrating
      'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1200&q=80', // diverse team high five
      'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=1200&q=80', // diverse professional team
      'https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=1200&q=80', // woman on stage speaking
      'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=1200&q=80', // diverse professionals laughing
      'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=1200&q=80', // black man artist in studio
      'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=1200&q=80', // woman of color in business
      'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=1200&q=80', // performer on stage
      'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1200&q=80', // tech professional at work
      'https://images.unsplash.com/photo-1519834785169-98be25ec3f84?w=1200&q=80', // diverse group celebrating
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200&q=80', // male professional portrait
      'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=1200&q=80', // confident business woman
      'https://images.unsplash.com/photo-1598966739654-5e9ecb8d4a62?w=1200&q=80', // asian woman in creative field
      'https://images.unsplash.com/photo-1556155092-490a1ba16284?w=1200&q=80', // diverse audience listening
      'https://images.unsplash.com/photo-1571513800374-df1bbe650e56?w=1200&q=80', // craftsperson at work
      'https://images.unsplash.com/photo-1595666944516-bbb485958fb5?w=1200&q=80', // musician performing
      'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&q=80', // success achievement
      'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&q=80', // diverse team collaboration
      'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1200&q=80', // celebrating professional win
      'https://images.unsplash.com/photo-1573496774426-fe3db3dd1731?w=1200&q=80', // latina businesswoman
      'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=1200&q=80', // diverse medical team
      'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&q=80', // creative team brainstorming
      'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=1200&q=80', // public speaking success
      'https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=1200&q=80', // photographer professional
      'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=1200&q=80' // diverse professionals networking
    ],
    suggestedTemplate: REEL_TEMPLATES[2],
    color: 'from-ring to-accent',
    representativeImage: 'https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=400&q=80'
  },
  {
    id: 'health',
    title: 'Perfect Health',
    icon: <Zap className="w-6 h-6" />,
    category: 'Health',
    description: 'Radiate vitality, energy, and optimal wellness',
    affirmations: [
      'I am radiating perfect health and vitality',
      'My body is strong, powerful, and resilient',
      'Limitless energy flows through every cell',
      'I glow with vibrant, unstoppable wellness',
      'Perfect health is my natural state now',
      'I am alive with extraordinary vitality',
      'My cells regenerate with optimal perfection',
      'I feel absolutely incredible every day',
      'Vibrant energy surges through my entire being',
      'My body heals rapidly and completely',
      'I am fit, strong, and magnificently healthy',
      'Optimal wellness is mine right now',
      'I thrive with boundless, radiant energy',
      'My immune system is powerfully invincible',
      'I radiate magnetic health and beauty',
      'My body loves me and serves me perfectly',
      'I am living in my healthiest body ever',
      'Pure wellness flows through every part of me',
      'I am my strongest, healthiest self today',
      'Explosive energy and vitality are mine now'
    ],
    subliminalWords: ['healthy', 'vital', 'strong', 'energised', 'fit', 'vibrant', 'thriving', 'powerful'],
    sampleImages: [
      'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1200&q=80', // person running sunrise
      'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=1200&q=80', // yoga sunrise
      'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1200&q=80', // athlete celebrating
      'https://images.unsplash.com/photo-1519311965067-36d3e5f33d39?w=1200&q=80',
      'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=1200&q=80',
      'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=1200&q=80',
      'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=1200&q=80',
      'https://images.unsplash.com/photo-1506126279646-a697353d3166?w=1200&q=80',
      'https://images.unsplash.com/photo-1538805060514-97d9cc17730c?w=1200&q=80',
      'https://images.unsplash.com/photo-1518611012118-696072aa579a?w=1200&q=80',
      'https://images.unsplash.com/photo-1588286840104-8957b019727f?w=1200&q=80',
      'https://images.unsplash.com/photo-1532384748853-8f54a8f476e2?w=1200&q=80',
      'https://images.unsplash.com/photo-1467003909585-2f8a72700288?w=1200&q=80',
      'https://images.unsplash.com/photo-1542990253-a781e04c0082?w=1200&q=80',
      'https://images.unsplash.com/photo-1584735175097-719d848f8449?w=1200&q=80',
      'https://images.unsplash.com/photo-1593476087123-36d1de271f08?w=1200&q=80',
      'https://images.unsplash.com/photo-1540206395-68808572332f?w=1200&q=80',
      'https://images.unsplash.com/photo-1552196563-55cd4e45efb3?w=1200&q=80',
      'https://images.unsplash.com/photo-1530822847156-5df684ec5ee1?w=1200&q=80',
      'https://images.unsplash.com/photo-1483721310020-03333e577078?w=1200&q=80',
      'https://images.unsplash.com/photo-1485727749690-d091e8284ef3?w=1200&q=80',
      'https://images.unsplash.com/photo-1522844990619-4951c40f7eda?w=1200&q=80',
      'https://images.unsplash.com/photo-1594911772125-07fc7a2d8d9f?w=1200&q=80',
      'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=1200&q=80',
      'https://images.unsplash.com/photo-1526506118085-60ce8714f8c5?w=1200&q=80',
      'https://images.unsplash.com/photo-1511688878353-3a2f5be94cd7?w=1200&q=80',
      'https://images.unsplash.com/photo-1528822163949-063bb59fe8c5?w=1200&q=80',
      'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=1200&q=80',
      'https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=1200&q=80',
      'https://images.unsplash.com/photo-1515003197210-e0cd71810b5f?w=1200&q=80'
    ],
    suggestedTemplate: REEL_TEMPLATES[3],
    color: 'from-primary to-muted',
    representativeImage: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&q=80'
  },
  {
    id: 'confidence',
    title: 'Magnetic Confidence',
    icon: <Flame className="w-6 h-6" />,
    category: 'Self',
    description: 'Embody unstoppable confidence and self-worth',
    affirmations: [
      'I am absolutely unstoppable and powerful',
      'Magnetic confidence radiates from my core',
      'I am irresistibly charismatic and captivating',
      'People are naturally drawn to my energy',
      'I own my power completely and unapologetically',
      'I am fearless in pursuit of my dreams',
      'I command respect and admiration effortlessly',
      'My presence is electric and unforgettable',
      'I walk with unshakeable confidence',
      'I am bold, powerful, and extraordinary',
      'My magnetic energy attracts incredible people',
      'I believe in myself with absolute certainty',
      'I am authentically and unapologetically myself',
      'Limitless confidence flows through me',
      'I am an unstoppable force of nature',
      'I captivate and inspire everyone I meet',
      'I am completely self-assured in who I am',
      'My energy transforms every room I enter',
      'I radiate unstoppable power and confidence',
      'I am unshakeable, magnetic, and magnificent'
    ],
    subliminalWords: ['confident', 'powerful', 'magnetic', 'charismatic', 'bold', 'fearless', 'unstoppable', 'commanding'],
    sampleImages: [
      'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=1200&q=80', // woman speaking on stage to crowd
      'https://images.unsplash.com/photo-1517849845537-4d257902454a?w=1200&q=80', // person hiking mountain peak success
      'https://images.unsplash.com/photo-1542766788-a2f588f447ee?w=1200&q=80', // person dancing freely
      'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=1200&q=80', // performer on stage confident
      'https://images.unsplash.com/photo-1519834785169-98be25ec3f84?w=1200&q=80', // diverse friends laughing
      'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=1200&q=80', // confident woman of color smiling
      'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=1200&q=80', // woman smiling confidently
      'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=1200&q=80', // diverse team celebrating
      'https://images.unsplash.com/photo-1502823403499-6ccfcf4fb453?w=1200&q=80', // person meditating on mountain
      'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=1200&q=80', // confident man portrait
      'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=1200&q=80', // woman arms outstretched nature
      'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=1200&q=80', // strong business woman
      'https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=1200&q=80', // woman on stage speaking
      'https://images.unsplash.com/photo-1485178575877-1a13bf489dfe?w=1200&q=80', // surfer riding wave
      'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=1200&q=80', // professional confident portrait
      'https://images.unsplash.com/photo-1515169067868-5387ec356754?w=1200&q=80', // public speaker to audience
      'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=1200&q=80', // group friends celebrating
      'https://images.unsplash.com/photo-1499996860823-5214fcc65f8f?w=1200&q=80', // man smiling confidently
      'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=1200&q=80', // woman confident face portrait
      'https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=1200&q=80', // artist creating confidently
      'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1200&q=80', // runner athlete triumph
      'https://images.unsplash.com/photo-1556155092-490a1ba16284?w=1200&q=80', // speaker presenting to crowd
      'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&q=80', // team working together confidently
      'https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?w=1200&q=80', // woman in graduation gown
      'https://images.unsplash.com/photo-1573496773905-f5b17e717f05?w=1200&q=80', // woman presenting with confidence
      'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=1200&q=80', // business woman confident
      'https://images.unsplash.com/photo-1518105779142-d975f22f1b0a?w=1200&q=80', // person standing confident outdoors
      'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&q=80', // diverse team high five
      'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1200&q=80', // startup team celebrating
      'https://images.unsplash.com/photo-1523438885200-e635ba2c371e?w=1200&q=80' // woman confident outdoors
    ],
    suggestedTemplate: REEL_TEMPLATES[4],
    color: 'from-secondary to-accent',
    representativeImage: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&q=80'
  },
  {
    id: 'dream_life',
    title: 'Ultimate Dream Life',
    icon: <Home className="w-6 h-6" />,
    category: 'Lifestyle',
    description: 'Live your dream life in every area',
    representativeImage: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400&q=80',
    affirmations: [
      'I am living my ultimate dream life right now',
      'Everything aligns perfectly for my highest good',
      'My ideal reality manifests effortlessly',
      'I wake up grateful for this extraordinary life',
      'My dreams become reality with divine timing',
      'Life flows to me with grace and abundance',
      'I am surrounded by beauty and prosperity',
      'My perfect life unfolds magnificently each day',
      'I deserve and receive everything I desire',
      'My reality exceeds my wildest dreams',
      'I am free, fulfilled, and living authentically',
      'Joy and abundance are my natural state',
      'I create magic in every moment',
      'My life is a beautiful masterpiece unfolding',
      'Everything I need comes to me easily',
      'I am blessed beyond measure',
      'My perfect life is here now',
      'I thrive in all areas of my life',
      'Miracles happen for me daily',
      'I am living the life of my dreams'
    ],
    subliminalWords: ['freedom', 'joy', 'perfect', 'blessed', 'fulfilled', 'thriving', 'abundant', 'magical'],
    sampleImages: [
      'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200&q=80', // tropical paradise beach
      'https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=1200&q=80', // beautiful nature freedom
      'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1200&q=80', // travel adventure
      'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=1200&q=80', // lake peaceful living
      'https://images.unsplash.com/photo-1545342323-2d0d22d4e0d1?w=1200&q=80', // paradise aerial view
      'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=1200&q=80', // beach paradise sunset
      'https://images.unsplash.com/photo-1523878288860-7ad281611901?w=1200&q=80', // luxury beachfront home
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&q=80', // mountain lake serenity
      'https://images.unsplash.com/photo-1471086569966-db3eebc25a59?w=1200&q=80', // swiss alps landscape
      'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=1200&q=80', // mountain adventure
      'https://images.unsplash.com/photo-1534951009808-766178b47a4f?w=1200&q=80', // dubai luxury lifestyle
      'https://images.unsplash.com/photo-1561414927-6d86591d0c4f?w=1200&q=80', // maldives overwater villa
      'https://images.unsplash.com/photo-1540202404-aef7d68b9f9b?w=1200&q=80', // luxury yacht ocean
      'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=1200&q=80', // private jet interior
      'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=1200&q=80', // spa wellness lifestyle
      'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=1200&q=80', // modern luxury home
      'https://images.unsplash.com/photo-1574169208507-84376144848b?w=1200&q=80', // ocean luxury sunset
      'https://images.unsplash.com/photo-1539635278303-d4002c07eae3?w=1200&q=80', // tropical island paradise
      'https://images.unsplash.com/photo-1509023464722-18d996393ca8?w=1200&q=80', // maldives luxury resort
      'https://images.unsplash.com/photo-1583286814049-6ccf5b2ff490?w=1200&q=80', // white yacht blue water
      'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=1200&q=80', // yacht turquoise ocean
      'https://images.unsplash.com/photo-1596003906949-67221c37965c?w=1200&q=80', // luxury penthouse view
      'https://images.unsplash.com/photo-1605902394069-ff2ae2430e62?w=1200&q=80', // ferrari luxury car
      'https://images.unsplash.com/photo-1540541338287-41700207dee6?w=1200&q=80', // luxury sports car coast
      'https://images.unsplash.com/photo-1552960394-c81add8de8b8?w=1200&q=80', // champagne celebration
      'https://images.unsplash.com/photo-1505142468610-359e7d316be0?w=1200&q=80', // mountain top sunrise
      'https://images.unsplash.com/photo-1518895949257-7621c3c786d7?w=1200&q=80', // bali villa pool
      'https://images.unsplash.com/photo-1540541338287-41700207dee6?w=1200&q=80', // coastal luxury drive
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&q=80', // mountain lake reflection
      'https://images.unsplash.com/photo-1611117775350-ac3950990985?w=1200&q=80' // luxury helicopter view
    ],
    suggestedTemplate: REEL_TEMPLATES[0], // Therapeutic Calm
    color: 'from-primary to-accent'
  }
];

type View = 'library' | 'options' | 'play' | 'customize' | 'scratch';

export default function ManifestationTemplateLibrary() {
  const [currentView, setCurrentView] = useState<View>('library');
  const [selectedTemplate, setSelectedTemplate] = useState<ManifestationTemplate | null>(null);
  const [playingReel, setPlayingReel] = useState<any>(null);

  const handleTemplateSelect = (template: ManifestationTemplate) => {
    setSelectedTemplate(template);
    setCurrentView('options');
  };

  const handlePlayAsIs = () => {
    if (!selectedTemplate) return;
    
    const reel = {
      id: 'preview',
      title: selectedTemplate.title,
      affirmations: selectedTemplate.affirmations,
      subliminal_words: selectedTemplate.subliminalWords,
      media_files: selectedTemplate.sampleImages.map(url => ({
        type: 'image' as const,
        url,
        duration: 5
      })),
      template: selectedTemplate.suggestedTemplate
    };
    
    setPlayingReel(reel);
    setCurrentView('play');
  };

  const handleCustomize = () => {
    setCurrentView('customize');
  };

  const handleCreateFromScratch = () => {
    setSelectedTemplate(null);
    setCurrentView('scratch');
  };

  const handleBack = () => {
    if (currentView === 'options') {
      setCurrentView('library');
      setSelectedTemplate(null);
    } else if (currentView === 'customize' || currentView === 'scratch') {
      setCurrentView('library');
      setSelectedTemplate(null);
    } else if (currentView === 'play') {
      setCurrentView('options');
      setPlayingReel(null);
    }
  };

  // Playing a reel
  if (currentView === 'play' && playingReel) {
    return (
      <EnhancedManifestationPlayer
        reel={playingReel}
        onClose={handleBack}
      />
    );
  }

  // Customise or create from scratch
  if (currentView === 'customize' || currentView === 'scratch') {
    return (
      <div className="space-y-4">
        <Button
          onClick={handleBack}
          variant="ghost"
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Templates
        </Button>
        <EnhancedManifestationCreator
          initialTemplate={currentView === 'customize' ? selectedTemplate : undefined}
        />
      </div>
    );
  }

  // Options for selected template
  if (currentView === 'options' && selectedTemplate) {
    return (
      <div className="space-y-6">
        <Button
          onClick={handleBack}
          variant="ghost"
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Templates
        </Button>

        <Card className={`bg-gradient-to-br ${selectedTemplate.color} border-0 overflow-hidden`}>
          <CardContent className="p-8 text-white">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-lg">
                {selectedTemplate.icon}
              </div>
              <div>
                <Badge className="mb-2 bg-white/20 text-white border-white/30">
                  {selectedTemplate.category}
                </Badge>
                <h2 className="text-3xl font-bold mb-2">{selectedTemplate.title}</h2>
                <p className="text-white/90">{selectedTemplate.description}</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-4">
              {selectedTemplate.sampleImages.slice(0, 3).map((img, i) => (
                <div key={i} className="aspect-video rounded-lg overflow-hidden">
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <p className="text-sm font-semibold">Sample Affirmations:</p>
              <div className="flex flex-wrap gap-2">
                {selectedTemplate.affirmations.slice(0, 3).map((aff, i) => (
                  <Badge key={i} variant="outline" className="bg-white/10 text-white border-white/30">
                    {aff}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-primary" onClick={handlePlayAsIs}>
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Play className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-bold text-lg mb-2">Use As-Is</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Play this manifestation immediately with pre-selected content
              </p>
              <Badge variant="secondary">Quick Start</Badge>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-primary" onClick={handleCustomize}>
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Edit className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-bold text-lg mb-2">Customise</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Modify affirmations, add your own images, and personalise music
              </p>
              <Badge variant="secondary">Personalised</Badge>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-primary" onClick={handleCreateFromScratch}>
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Wand2 className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-bold text-lg mb-2">From Scratch</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create your own unique manifestation reel from the ground up
              </p>
              <Badge variant="secondary">Full Control</Badge>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Template library
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold mb-3 flex items-center justify-center gap-2">
          <Sparkles className="w-8 h-8 text-primary" />
          Manifestation Templates
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Choose from powerful pre-made manifestation reels designed to reprogramme your subconscious mind
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {MANIFESTATION_TEMPLATES.map((template) => (
          <Card
            key={template.id}
            className="hover:shadow-lg transition-all cursor-pointer group hover:scale-[1.02] overflow-hidden"
            onClick={() => handleTemplateSelect(template)}
          >
            <div className={`h-40 bg-gradient-to-br ${template.color} flex items-center justify-center relative overflow-hidden`}>
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              <div className="relative text-white transform group-hover:scale-110 transition-transform">
                {template.icon}
              </div>
            </div>
            
            <CardContent className="p-5">
              <Badge className="mb-2" variant="secondary">
                {template.category}
              </Badge>
              <h3 className="font-bold text-lg mb-2">{template.title}</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {template.description}
              </p>
              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">
                  {template.affirmations.length} affirmations
                </div>
                <Button size="sm" variant="outline">
                  Select
                  <ArrowLeft className="w-3 h-3 ml-1 rotate-180" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-dashed">
        <CardContent className="p-8 text-center">
          <Wand2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="font-semibold text-lg mb-2">Want Full Control?</h3>
          <p className="text-muted-foreground mb-4">
            Create your own manifestation reel from scratch with complete customisation
          </p>
          <Button onClick={handleCreateFromScratch}>
            Start From Scratch
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
